import { ArrowDown, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n/LanguageProvider";

const VIDEO_URL = "/media/hero.mp4?v=3";
const POSTER_URL = "/media/hero-poster.jpg";

export default function Hero() {
    const { t } = useT();
    const videoRef = useRef(null);
    const [videoReady, setVideoReady] = useState(false);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.setAttribute("playsinline", "");
        v.setAttribute("webkit-playsinline", "");
        v.setAttribute("muted", "");
        v.setAttribute("autoplay", "");
        v.removeAttribute("controls");

        const tryPlay = () => {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        const onPlaying = () => setVideoReady(true);

        tryPlay();
        v.addEventListener("loadedmetadata", tryPlay);
        v.addEventListener("canplay", tryPlay);
        v.addEventListener("playing", onPlaying);
        document.addEventListener("visibilitychange", tryPlay);
        const userKick = () => tryPlay();
        window.addEventListener("touchstart", userKick, { once: true, passive: true });
        window.addEventListener("click", userKick, { once: true });

        return () => {
            v.removeEventListener("loadedmetadata", tryPlay);
            v.removeEventListener("canplay", tryPlay);
            v.removeEventListener("playing", onPlaying);
            document.removeEventListener("visibilitychange", tryPlay);
            window.removeEventListener("touchstart", userKick);
            window.removeEventListener("click", userKick);
        };
    }, []);

    return (
        <section
            id="top"
            data-testid="hero-section"
            aria-labelledby="hero-headline"
            className="relative min-h-screen pt-28 md:pt-32 pb-24 overflow-hidden text-primary-foreground"
        >
            <div
                className="absolute inset-0 -z-10 overflow-hidden pointer-events-none select-none bg-primary"
                style={{
                    backgroundImage: `url(${POSTER_URL})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <video
                    ref={videoRef}
                    data-testid="hero-bg-video"
                    className="w-full h-full object-cover pointer-events-none transition-opacity duration-700"
                    style={{ opacity: videoReady ? 1 : 0 }}
                    src={VIDEO_URL}
                    poster={POSTER_URL}
                    autoPlay
                    loop
                    muted
                    playsInline
                    disablePictureInPicture
                    disableRemotePlayback
                    preload="auto"
                    tabIndex={-1}
                    aria-hidden="true"
                />
            </div>
            <div className="absolute inset-0 -z-10 bg-primary/55 pointer-events-none" aria-hidden="true" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/30 via-primary/50 to-primary/85 pointer-events-none" aria-hidden="true" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-center gap-3 text-xs md:text-sm uppercase tracking-[0.25em] text-accent font-semibold fade-up">
                    <span className="block w-10 h-px bg-accent" aria-hidden="true" />
                    {t("hero.eyebrow")}
                </div>

                <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 items-end">
                    <h1
                        id="hero-headline"
                        data-testid="hero-headline"
                        className="col-span-12 lg:col-span-9 font-display text-[18vw] sm:text-[14vw] md:text-[11vw] lg:text-[9rem] xl:text-[11rem] leading-[0.85] tracking-tighter text-primary-foreground fade-up"
                    >
                        {t("hero.headline.line1")}
                        <span className="block">
                            {t("hero.headline.line2.prefix")}{" "}
                            <em className="not-italic text-accent">
                                {t("hero.headline.line2.accent")}
                            </em>
                            .
                        </span>
                    </h1>

                    <div
                        className="col-span-12 lg:col-span-3 lg:pb-6 fade-up"
                        style={{ animationDelay: "0.2s" }}
                    >
                        <p className="font-display italic text-xl md:text-2xl text-primary-foreground/85 leading-snug max-w-sm">
                            {t("hero.tagline")}
                        </p>
                    </div>
                </div>

                <p
                    data-testid="hero-subline"
                    className="mt-10 md:mt-14 max-w-2xl text-lg md:text-2xl text-primary-foreground/90 leading-snug fade-up font-light"
                    style={{ animationDelay: "0.3s" }}
                >
                    {t("hero.subline")}
                </p>

                <div
                    className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-7 fade-up"
                    style={{ animationDelay: "0.45s" }}
                >
                    <a
                        href="#waitlist"
                        data-testid="hero-primary-cta"
                        className="relative inline-flex items-center gap-3 bg-accent text-primary-foreground pl-8 pr-7 py-4 rounded-full text-sm md:text-base tracking-wide overflow-hidden cta-glow shadow-[0_18px_40px_-12px_rgba(198,107,72,0.55)] hover:shadow-[0_24px_60px_-12px_rgba(198,107,72,0.75)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-foreground"
                    >
                        <span className="relative z-10">{t("hero.cta.primary")}</span>
                        <span className="relative z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-foreground/15 backdrop-blur-sm transition-transform duration-300 group-hover:translate-x-1">
                            <ArrowRight size={14} />
                        </span>
                        <span aria-hidden="true" className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-accent via-accent/90 to-accent transition-transform duration-700 ease-out" />
                    </a>
                    <a
                        href="#story"
                        data-testid="hero-secondary-cta"
                        className="inline-flex items-center gap-3 text-primary-foreground/85 hover:text-accent transition-colors text-sm md:text-base tracking-wide group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
                    >
                        <span className="border-b border-primary-foreground/40 group-hover:border-accent pb-0.5">
                            {t("hero.cta.secondary")}
                        </span>
                    </a>
                </div>

                <div
                    className="mt-20 md:mt-28 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary-foreground/70 fade-up"
                    style={{ animationDelay: "0.6s" }}
                    aria-hidden="true"
                >
                    <ArrowDown size={14} className="animate-bounce" />
                    {t("hero.scroll")}
                </div>
            </div>
        </section>
    );
}
